/**
 * src/App.tsx
 * 
 * Main application component that sets up routing for the entire application.
 * Updated to implement a role-first authentication flow:
 * 1. User selects their role (coach or athlete)
 * 2. User chooses to login or register
 * 3. System handles role-specific flows (e.g., invitation code for athletes)
 * 4. User is redirected to the appropriate dashboard based on their role
 * Updated to integrate coach subscription system - wraps coach routes with subscription wrapper.
 */

import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout';
import { ThemeProvider } from './components/ThemeProvider';
import { ModalProvider, ProfileProvider } from './contexts';
import { I18nProvider } from './lib/i18n/context';

// Lazy load pages for better performance
import { lazy, Suspense, useEffect, useState } from 'react';

// Auth components
import AuthCallback from './components/auth/AuthCallback';
import InviteCodeEntry from './components/auth/InviteCodeEntry';
import LoginSuccess from './components/auth/LoginSuccess';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Login from './components/Login';
import Register from './components/Register';
import RoleSelection from './components/RoleSelection';
import { useStandaloneRedirect } from './hooks/useStandaloneRedirect';
import { EmailConfirmation } from './pages/auth/EmailConfirmation';

// Coach dashboard wrapper for subscription control
import { CoachDashboardWrapper } from './components/coach/CoachDashboardWrapper';

// Coach pages
const CoachDashboard = lazy(() => import('./pages/coach/Dashboard').then(module => ({ default: module.CoachDashboard })));
const ProgramDashboard = lazy(() => import('./pages/coach/ProgramDashboard').then(module => ({ default: module.ProgramDashboard })));
const Workouts = lazy(() => import('./pages/coach/Workouts').then(module => ({ default: module.Workouts })));
const Athletes = lazy(() => import('./pages/coach/Athletes').then(module => ({ default: module.Athletes })));
const CoachAthleteProfile = lazy(() => import('./pages/coach/AthleteProfile').then(module => ({ default: module.AthleteProfile })));
const AthleteCalendar = lazy(() => import('./pages/coach/AthleteCalendar').then(module => ({ default: module.AthleteCalendar })));
const ProgramCalendar = lazy(() => import('./pages/coach/ProgramCalendar').then(module => ({ default: module.ProgramCalendar })));
const ProgramAssignment = lazy(() => import('./pages/coach/ProgramAssignment').then(module => ({ default: module.ProgramAssignment })));
const CoachAccount = lazy(() => import('./pages/coach/Account').then(module => ({ default: module.CoachAccount })));

// Athlete pages
const AthleteDashboard = lazy(() => import('./pages/athlete/Dashboard').then(module => ({ default: module.AthleteDashboard })));
const AthleteAccount = lazy(() => import('./pages/athlete/Account').then(module => ({ default: module.AthleteAccount })));

// Demo pages
const DemoCoachDashboard = lazy(() => import('./pages/demo/Dashboard').then(module => ({ default: module.DemoCoachDashboard })));

// Simple loading screen component
const LoadingScreen = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
      <p className="text-gray-600 dark:text-gray-400 text-sm">Loading...</p>
    </div>
  </div>
);

// Redirect component that preserves URL parameters
const RedirectWithParams = ({ to }: { to: string }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  
  // If there's a 'code' parameter, redirect to register with the code
  if (searchParams.has('code')) {
    return <Navigate to={`/register/athlete?code=${searchParams.get('code')}`} replace />;
  }
  
  // Otherwise, just redirect to the target path
  return <Navigate to={to} replace />;
};

// Coach routes component with ModalProvider for coach pages
const CoachRoutes = () => (
  <ModalProvider>
    <CoachDashboardWrapper showLimitedAccess={false}>
      <Routes>
        <Route path="/" element={<CoachDashboard />} />
        <Route path="/programs" element={<ProgramDashboard />} />
        <Route path="/workouts" element={<Workouts />} />
        <Route path="/athletes" element={<Athletes />} />
        <Route path="/athlete/:athleteId" element={<CoachAthleteProfile />} />
        <Route path="/athlete/:athleteId/calendar" element={<AthleteCalendar />} />
        <Route path="/program/:programId" element={<ProgramCalendar />} />
        <Route path="/program/:programId/assign" element={<ProgramAssignment />} />
        <Route path="/account" element={<CoachAccount />} />
      </Routes>
    </CoachDashboardWrapper>
  </ModalProvider>
);

// Athlete routes component with ModalProvider for athlete pages
const AthleteRoutes = () => (
  <ModalProvider>
    <Routes>
      <Route path="/" element={<AthleteDashboard />} />
      <Route path="/account" element={<AthleteAccount />} />
    </Routes>
  </ModalProvider>
);

// Simple demo routes for now
const DemoRoutes = () => (
  <Routes>
    <Route path="/" element={<DemoCoachDashboard />} />
  </Routes>
);

// PWA detection and routing component
const PWAHomeRoute = () => {
  const [isPWA, setIsPWA] = useState<boolean | null>(null);
  const location = useLocation();

  useEffect(() => {
    // Detect if app is running as PWA
    const detectPWA = () => {
      // Check if user wants to force web view
      const searchParams = new URLSearchParams(location.search);
      const forceWeb = searchParams.get('web') === 'true';
      
      if (forceWeb) {
        setIsPWA(false);
        return;
      }
      
      // Multiple methods to detect PWA/standalone mode
      const isStandaloneDisplayMode = window.matchMedia('(display-mode: standalone)').matches;
      const isIOSStandalone = (window.navigator as any).standalone === true;
      const isAndroidApp = document.referrer.includes('android-app://');
      
      // Check if launched from home screen (iOS Safari)
      const isFromHomeScreen = isIOSStandalone || isStandaloneDisplayMode;
      
      // Additional check for Chrome/Android PWA
      const isMinimalUI = window.matchMedia('(display-mode: minimal-ui)').matches;
      
      const isPWAMode = isStandaloneDisplayMode || isIOSStandalone || isAndroidApp || isMinimalUI;
      
      // Debug logging (can be removed in production)
      console.log('PWA Detection:', {
        isStandaloneDisplayMode,
        isIOSStandalone,
        isAndroidApp,
        isMinimalUI,
        isFromHomeScreen,
        forceWeb,
        finalDecision: isPWAMode
      });
      
      setIsPWA(isPWAMode);
    };

    // Small delay to ensure DOM is ready
    setTimeout(detectPWA, 100);
  }, [location.search]);

  // Show loading while detecting PWA mode
  if (isPWA === null) {
    return <LoadingScreen />;
  }

  // If it's a PWA, redirect to athlete login
  if (isPWA) {
    return <Navigate to="/login/athlete" replace />;
  }

  // For web access, redirect to login page instead of showing landing page
  return <Navigate to="/login" replace />;
};

function App() {
  useStandaloneRedirect();
  
  return (
    <ThemeProvider>
      <I18nProvider>
        <ProfileProvider>
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              <Route path="/" element={<PWAHomeRoute />} />
              
              {/* New role-first authentication flow */}
              <Route path="/login" element={<RoleSelection />} />
              <Route path="/login/:role" element={<Login />} />
              <Route path="/register/:role" element={<Register />} />
              
              {/* Legacy routes for backward compatibility */}
              <Route path="/auth" element={<Navigate to="/login" replace />} />
              <Route path="/register" element={<Register />} />
              
              {/* Legacy route redirects */}
              <Route path="/athlete-signin" element={<RedirectWithParams to="/login" />} />
              <Route path="/athlete-signin/:coachName" element={<RedirectWithParams to="/login" />} />
              <Route path="/coach-signin" element={<Navigate to="/login" replace />} />
              
              <Route path="/login/callback" element={<AuthCallback />} />
              <Route path="/login/confirm" element={<EmailConfirmation />} />
              <Route path="/login/success" element={<LoginSuccess />} />
              
              {/* Protected route for invite code entry */}
              <Route 
                path="/invite-code-entry" 
                element={
                  <ProtectedRoute role="athlete">
                    <InviteCodeEntry />
                  </ProtectedRoute>
                } 
              />
              
              {/* Demo Routes with Sample Data */}
              <Route 
                path="/demo/*" 
                element={
                  <Layout>
                    <DemoRoutes />
                  </Layout>
                } 
              />
              
              {/* Protected Coach Routes */}
              <Route 
                path="/coach/*" 
                element={
                  <ProtectedRoute role="coach">
                    <Layout>
                      <CoachRoutes />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              
              {/* Protected Athlete Routes */}
              <Route 
                path="/athlete/*" 
                element={
                  <ProtectedRoute role="athlete">
                    <Layout>
                      <AthleteRoutes />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </ProfileProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}

export default App;